import React, { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle2, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import axios from 'axios';
import confetti from 'canvas-confetti';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const BookingSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const [status, setStatus] = useState('checking');
  const [bookingDetails, setBookingDetails] = useState(null);

  useEffect(() => {
    const isManual = searchParams.get('confirmed') === 'manual';
    
    if (isManual) {
      setStatus('success');
      setBookingDetails({ manual: true });
      confetti({
        particleCount: 100,
        spread: 70,
        origin: { y: 0.6 },
        colors: ['#F56F05', '#000000', '#FFFFFF']
      });
      return;
    }

    const checkPaymentStatus = async () => {
      if (!sessionId) {
        setStatus('error');
        return;
      }

      try {
        const response = await axios.get(`${API_URL}/api/payments/status/${sessionId}`);
        
        if (response.data.payment_status === 'paid') {
          setStatus('success');
          setBookingDetails(response.data);
          
          confetti({
            particleCount: 100,
            spread: 70,
            origin: { y: 0.6 },
            colors: ['#F56F05', '#000000', '#FFFFFF']
          });
        } else if (response.data.status === 'expired') {
          setStatus('expired');
        } else {
          setTimeout(checkPaymentStatus, 2000);
        }
      } catch (_) {
        setStatus('error');
      }
    };

    checkPaymentStatus();
  }, [sessionId, searchParams]);

  if (status === 'checking') {
    return (
      <div className="min-h-screen pt-24 flex items-center justify-center bg-zinc-50">
        <div className="text-center">
          <Loader2 className="h-12 w-12 text-[#F56F05] animate-spin mx-auto mb-4" />
          <p className="text-lg text-zinc-600">Confirming your payment...</p>
        </div>
      </div>
    );
  }

  if (status === 'error' || status === 'expired') {
    return (
      <div className="min-h-screen pt-24 flex items-center justify-center bg-zinc-50">
        <div className="text-center max-w-md px-6">
          <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-6">
            <span className="text-3xl">❌</span>
          </div>
          <h1 className="text-2xl font-bold text-zinc-900 mb-4">
            {status === 'expired' ? 'Session Expired' : 'Something Went Wrong'}
          </h1>
          <p className="text-zinc-600 mb-8">
            {status === 'expired' 
              ? 'Your payment session has expired. Please try booking again.'
              : 'We couldn\'t verify your payment. Please contact us if you were charged.'}
          </p>
          <Button asChild className="bg-[#F56F05] hover:bg-[#C2410C]">
            <Link to="/booking">Try Again</Link>
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24 flex items-center justify-center bg-zinc-50">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="text-center max-w-lg px-6"
      >
        <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6">
          <CheckCircle2 className="h-10 w-10 text-green-600" />
        </div>
        
        <h1 className="text-3xl font-bold text-zinc-900 font-heading mb-4" data-testid="booking-success-title">
          Booking Confirmed!
        </h1>
        
        <p className="text-lg text-zinc-600 mb-8">
          Thank you for choosing Gleam Guru Cleaning. We've received your booking and payment.
        </p>

        <div className="bg-white rounded-2xl p-6 border border-zinc-200 mb-8 text-left">
          <h3 className="font-bold text-zinc-900 mb-4">What's Next?</h3>
          <ul className="space-y-3 text-zinc-600">
            <li className="flex items-start gap-3">
              <CheckCircle2 className="h-5 w-5 text-[#F56F05] flex-shrink-0 mt-0.5" />
              <span>You'll receive a confirmation email shortly</span>
            </li>
            <li className="flex items-start gap-3">
              <CheckCircle2 className="h-5 w-5 text-[#F56F05] flex-shrink-0 mt-0.5" />
              <span>We'll confirm your appointment 24 hours before</span>
            </li>
            <li className="flex items-start gap-3">
              <CheckCircle2 className="h-5 w-5 text-[#F56F05] flex-shrink-0 mt-0.5" />
              <span>Our team will arrive at your scheduled time</span>
            </li>
          </ul>
        </div>

        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Button asChild className="bg-[#F56F05] hover:bg-[#C2410C]" data-testid="go-to-portal">
            <Link to="/portal">View My Bookings</Link>
          </Button>
          <Button asChild variant="outline" data-testid="go-home">
            <Link to="/">Back to Home</Link>
          </Button>
        </div>
      </motion.div>
    </div>
  );
};

export default BookingSuccessPage;
